import { Badge, Box, Center, HStack, Table, Text, VStack } from '@chakra-ui/react';
import { NewsBannerItemFragment } from '@client/graphql/types/graphql';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context';
import { useTranslate } from '@tolgee/react';
import { capitalize } from 'lodash';
import moment from 'moment-timezone';
import NewsBannerUpdateModal from './news-banner-update-modal';

interface Props {
  banners: NewsBannerItemFragment[];
}

export default function NewsBannerTable({ banners }: Props) {
  const { t } = useTranslate();
  const company = useFocusedCompanyContext();

  const formatTime = (time: string) => {
    return moment.tz(time, company.settings.timezone).format('DD/MM/YYYY HH:mm');
  };

  if (banners.length == 0) {
    return (
      <Center py={3}>
        <Text color={'gray.500'} fontStyle={'italic'}>
          {t('news-banner.alert.empty', 'News banner data is empty')}
        </Text>
      </Center>
    );
  }

  return (
    <Box>
      <Table.Root size="lg" interactive>
        <Table.Header>
          <Table.Row bgColor={'bg.subtle'}>
            {/* Desktop Headers */}
            <Table.ColumnHeader fontWeight={600} display={{ base: 'none', md: 'table-cell' }}>
              {t('news-banner.table.active', 'Status')}
            </Table.ColumnHeader>
            <Table.ColumnHeader fontWeight={600} display={{ base: 'none', md: 'table-cell' }}>
              {t('news-banner.table.title', 'Title')}
            </Table.ColumnHeader>
            <Table.ColumnHeader fontWeight={600} display={{ base: 'none', md: 'table-cell' }}>
              {t('news-banner.table.platforms', 'Platforms')}
            </Table.ColumnHeader>

            <Table.ColumnHeader fontWeight={600} display={{ base: 'none', md: 'table-cell' }}>
              {t('news-banner.table.time-range', 'Time Range')}
            </Table.ColumnHeader>

            {/* Mobile Headers */}
            <Table.ColumnHeader fontWeight={600} display={{ base: 'table-cell', md: 'none' }}>
              {t('news-banner.table.banner-info', 'Banner Information')}
            </Table.ColumnHeader>

            {/* Actions column - visible on both views */}
            <Table.ColumnHeader fontWeight={600} width="100px">
              {t('news-banner.table.actions', 'Actions')}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {banners.map((banner) => (
            <Table.Row key={banner.id}>
              {/* Desktop View */}
              <Table.Cell display={{ base: 'none', md: 'table-cell' }} borderBottom="1px solid" borderColor="gray.200">
                <Badge
                  color={'white'}
                  bgColor={banner.isActive ? 'green.500' : 'gray.300'}
                  variant="subtle"
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  {banner.isActive ? t('common.status.active', 'Active') : t('common.status.inactive', 'Inactive')}
                </Badge>
              </Table.Cell>
              <Table.Cell display={{ base: 'none', md: 'table-cell' }} borderBottom="1px solid" borderColor="gray.200">
                <Text maxW={'48'} truncate>
                  {banner.title}
                </Text>
              </Table.Cell>

              <Table.Cell display={{ base: 'none', md: 'table-cell' }} borderBottom="1px solid" borderColor="gray.200">
                {banner.platforms
                  .map((platform: string) => t(`banner-platform.${platform}`, capitalize(platform)))
                  .join(', ')}
              </Table.Cell>

              <Table.Cell display={{ base: 'none', md: 'table-cell' }} borderBottom="1px solid" borderColor="gray.200">
                <HStack gap={2}>
                  <Text>{formatTime(banner.startTime)}</Text>
                  <Text color="gray.500">→</Text>
                  <Text>{formatTime(banner.endTime)}</Text>
                </HStack>
              </Table.Cell>

              {/* Mobile View */}
              <Table.Cell display={{ base: 'table-cell', md: 'none' }} borderBottom="1px solid" borderColor="gray.200">
                <VStack align="start" gap={2}>
                  <HStack>
                    <Badge
                      color={'white'}
                      bgColor={banner.isActive ? 'green.500' : 'gray.300'}
                      variant="subtle"
                      px={2}
                      py={1}
                      borderRadius="full"
                    >
                      {banner.isActive ? t('common.status.active', 'Active') : t('common.status.inactive', 'Inactive')}
                    </Badge>
                  </HStack>
                  <Text fontWeight={'semibold'} maxW={'48'} truncate>
                    {banner.title}
                  </Text>
                  <Text>
                    {banner.platforms
                      .map((platform: string) => t(`banner-platform.${platform}`, capitalize(platform)))
                      .join(', ')}
                  </Text>
                  <VStack align="start" gap={0}>
                    <Text fontSize="sm">
                      {formatTime(banner.startTime)}{' '}
                      <Text as="span" color="gray.500">
                        →
                      </Text>
                    </Text>
                    <Text fontSize="sm">{formatTime(banner.endTime)}</Text>
                  </VStack>
                </VStack>
              </Table.Cell>

              {/* Actions - visible on both views */}
              <Table.Cell borderBottom="1px solid" borderColor="gray.200">
                <HStack gap={2}>
                  <NewsBannerUpdateModal banner={banner} />
                </HStack>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}
